import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router';
import { useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import Button from 'components/common/button';
import EmptyStateLayout from 'components/common/empty-state-layout';

/**
 * The catch-all used to redirect to `/` without saying anything, so a typo or a stale link looked like a
 * deliberate trip to the homepage. `forbidden.route` already reads "Page not found" for the 403, which is
 * the confusion this replaces rather than reuses - it keeps its own copy and stays on the legacy path.
 *
 * A real 404 is the one behaviour change in APO-483, and a route renders for everyone, so it is gated like
 * a structural restyle rather than shipped outright: switch-off keeps the silent redirect it has always
 * had (D7.7). The branch lives here rather than at the route so both arms are testable in one render.
 * APO-484 deletes the branch and leaves the page.
 */
const NotFoundPage: React.FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  if (!theme.isNewDesign) {
    return <Navigate replace to="/" />;
  }

  return (
    <CenteredEmptyState
      data-e2e="not-found"
      illustration="empty-state"
      titleId="not-found.title"
      description={<FormattedMessage id="not-found.description" />}
      action={
        <Button type="primary" data-e2e="not-found-go-home" onClick={() => navigate('/', { replace: true })}>
          <FormattedMessage id="not-found.go-home" />
        </Button>
      }
    />
  );
};

// Nothing above this page sets a height: it renders straight into MainLayout, without the InnerLayout
// every routed page gets. ErrorContainer centres itself for the same reason.
const CenteredEmptyState = styled(EmptyStateLayout)`
  height: 100%;
  justify-content: center;
`;

export default NotFoundPage;
